import { useCallback, useEffect, useState } from "react";
import { getJson, type Session } from "../api";
import { formatText, missingText, useTexts, type Label } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice as KitNotice, PageHead } from "../ui/kit";
import { PageState } from "../ui/pieces";
import styles from "./HomePage.module.css";

// Inicio (especificación §5.1, D18): cuatro indicadores y los avisos que piden acción, cada uno con su botón al lugar
// exacto donde se resuelve. Una fuente que falla dice «No se pudo cargar» con «Reintentar»; nunca 0 (H-3). El indicador de
// problemas y sus avisos solo existen con permiso de Error Hub (no se muestran en 0).

type Indicator = { ok: boolean; value: number | null; second?: number | null; third?: number | null };
type Notice = {
  kind: "key-failing" | "route-empty" | "provider-no-key" | "problem";
  title: string;
  detail: string | null;
  href: string;
  count?: number | null;
  users?: number | null;
  status?: string | null;
  module?: string | null;
};
type Home = {
  organizations: Indicator;
  ai: Indicator;
  problems: Indicator | null;
  alerts: Indicator;
  notices: Notice[];
  noticesComplete: boolean;
};

/** Singular o plural con su número (E18): «1 modelo», «61 modelos». */
export const plural = (L: Label, count: number, one: string, many: string, locale: string) =>
  formatText(L(count === 1 ? one : many), count.toLocaleString(locale));

/** Un texto opcional: si la clave no existe, no se muestra (nunca un código interno, D7). */
const optional = (L: Label, key: string) => {
  const value = L(key);
  return value === missingText(key) ? null : value;
};

export function HomePage({ session }: { session: Session }) {
  const { L } = useTexts();
  const [home, setHome] = useState<Home | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const load = useCallback(() => {
    void getJson<Home>("/api/admin/home").then((reply) => {
      if (reply.status === 200 && reply.body) {
        setHome(reply.body);
        setState("ready");
      } else setState("error");
    });
  }, []);
  useEffect(load, [load]);
  const firstName = (session.displayName ?? "").trim().split(/\s+/)[0] ?? "";
  const head = (
    <PageHead
      title={L("ADM_HOME_TITLE")}
      sub={firstName ? formatText(L("ADM_HOME_HELLO"), firstName) : L("ADM_HOME_HELLO_NONAME")}
    />
  );
  if (state === "error")
    return (
      <PageState
        head={head}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={load}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );
  return (
    <>
      {head}
      <section className={styles.indicators} aria-label={L("ADM_HOME_INDICATORS")} data-part="indicadores">
        {home ? <Indicators home={home} onRetry={load} /> : <IndicatorSkeleton count={4} />}
      </section>
      <section className={styles.notices} aria-labelledby="home-notices" data-part="avisos">
        <h2 id="home-notices" className={styles.subtitle}>
          {L("ADM_HOME_NOTICES")}
        </h2>
        {home ? <Notices home={home} onRetry={load} /> : <NoticeSkeleton />}
      </section>
    </>
  );
}

function Indicators({ home, onRetry }: { home: Home; onRetry: () => void }) {
  const { L, locale } = useTexts();
  const number = (value: number | null | undefined) => (value ?? 0).toLocaleString(locale);
  return (
    <>
      <Card
        label={L("ADM_HOME_ORGS")}
        href="/Organizations/Index"
        indicator={home.organizations}
        line={L("ADM_HOME_ORGS_LINE")}
        number={number}
        onRetry={onRetry}
      />
      <Card
        label={L("ADM_HOME_AI")}
        href="/Ai/Config/Index"
        indicator={home.ai}
        line={
          home.ai.ok
            ? `${plural(L, home.ai.second ?? 0, "ADM_N_MODELS_ONE", "ADM_N_MODELS", locale)} · ${plural(
                L,
                home.ai.third ?? 0,
                "ADM_N_PROVIDERS_ONE",
                "ADM_N_PROVIDERS",
                locale,
              )}`
            : ""
        }
        number={number}
        onRetry={onRetry}
      />
      {home.problems ? (
        <Card
          label={L("ADM_HOME_PROBLEMS")}
          href="/ErrorHub/Issues?status=ATTENTION"
          indicator={home.problems}
          line={L("ADM_HOME_PROBLEMS_LINE")}
          number={number}
          onRetry={onRetry}
        />
      ) : null}
      <Card
        label={L("ADM_HOME_ALERTS")}
        href="/Ai/Providers/Index"
        indicator={home.alerts}
        line={L("ADM_HOME_ALERTS_LINE")}
        number={number}
        onRetry={onRetry}
      />
    </>
  );
}

/** C32 · indicador: etiqueta, número y su contexto. Toda la tarjeta lleva al lugar del número. */
function Card({
  label,
  href,
  indicator,
  line,
  number,
  onRetry,
}: {
  label: string;
  href: string;
  indicator: Indicator;
  line: string;
  number: (value: number | null | undefined) => string;
  onRetry: () => void;
}) {
  const { L } = useTexts();
  if (!indicator.ok)
    return (
      <div className={styles.card} data-part="indicador-error">
        <span className={styles.cardLabel}>{label}</span>
        <span className={styles.cardFailed} role="status">
          {L("ADM_UNAVAILABLE")}
        </span>
        <button type="button" className={`tm-link ${styles.cardRetry}`} onClick={onRetry}>
          {L("ADM_RETRY")}
        </button>
      </div>
    );
  return (
    <a className={styles.card} href={href} data-part="indicador">
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.cardValue}>{number(indicator.value)}</span>
      <span className={styles.cardLine}>{line}</span>
    </a>
  );
}

function IndicatorSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={`${styles.card} ${styles.skeletonCard}`} aria-hidden="true">
          <span className={styles.bone} style={{ width: "45%" }} />
          <span className={styles.bone} style={{ width: "22%", height: 28 }} />
          <span className={styles.bone} style={{ width: "60%" }} />
        </div>
      ))}
    </>
  );
}

function NoticeSkeleton() {
  return (
    <ul className={styles.list} aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <li key={index} className={styles.row}>
          <span className={`${styles.icon} ${styles.boneIcon}`} />
          <span className={styles.rowText}>
            <span className={styles.bone} style={{ width: "50%" }} />
            <span className={styles.bone} style={{ width: "35%" }} />
          </span>
        </li>
      ))}
    </ul>
  );
}

const routeLabelKey: Record<string, string> = {
  TEXT_CHAT: "ADM_ROUTE_TEXT",
  VISION_CHAT: "ADM_ROUTE_VISION",
  EMBEDDING: "ADM_ROUTE_EMBEDDING",
};

function Notices({ home, onRetry }: { home: Home; onRetry: () => void }) {
  const { L, locale } = useTexts();
  const rows = home.notices.map((notice, index) => {
    const view = describe(notice, L, locale);
    return view ? (
      <li key={`${notice.kind}-${index}`} className={styles.row} data-part="aviso">
        <span className={`${styles.icon} ${styles[view.tone]}`} aria-hidden="true">
          <ChatIcon name={view.icon} />
        </span>
        <span className={styles.rowText}>
          <strong>{view.title}</strong>
          <span>{view.line}</span>
        </span>
        <a className="tm-btn secondary" href={notice.href}>
          {view.action}
        </a>
      </li>
    ) : null;
  });
  return (
    <>
      {!home.noticesComplete ? (
        <KitNotice
          tone="warning"
          action={
            <button type="button" className="tm-btn secondary" onClick={onRetry}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_HOME_PARTIAL")}
        </KitNotice>
      ) : null}
      {home.notices.length === 0 && home.noticesComplete ? (
        <div className="tm-empty" role="status" data-part="todo-en-orden">
          <strong>{L("ADM_HOME_EMPTY")}</strong>
          <span>{L("ADM_HOME_EMPTY_LINE")}</span>
        </div>
      ) : (
        <ul className={styles.list}>{rows}</ul>
      )}
    </>
  );
}

type NoticeView = { icon: string; tone: "danger" | "warning"; title: string; line: string; action: string };

/** El aviso en palabras del negocio: qué pasa, por qué importa y qué hacer (nunca un código, D7). */
function describe(notice: Notice, L: Label, locale: string): NoticeView | null {
  switch (notice.kind) {
    case "key-failing": {
      const count = notice.count ?? 0;
      const title = notice.detail
        ? formatText(L("ADM_NOTICE_KEY_FAILING"), notice.title, notice.detail, count.toLocaleString(locale))
        : formatText(L("ADM_NOTICE_KEY_FAILING_NOPROV"), notice.title, count.toLocaleString(locale));
      const auth = /AUTH|401|403|KEY/i.test(notice.status ?? "");
      return {
        icon: "triangle-alert",
        tone: "danger",
        title,
        line: `${L("ADM_NOTICE_CONNECTION")} · ${L(auth ? "ADM_REASON_AUTH" : "ADM_REASON_PROVIDER")}`,
        action: L("ADM_REVIEW"),
      };
    }
    case "route-empty": {
      const key = routeLabelKey[notice.title];
      if (!key) return null;
      return {
        icon: "route",
        tone: "warning",
        title: formatText(L("ADM_NOTICE_ROUTE_EMPTY"), L(key)),
        line: L(`${key}_EMPTY_LINE`),
        action: L("ADM_CHOOSE_MODEL"),
      };
    }
    case "provider-no-key":
      return {
        icon: "key-round",
        tone: "warning",
        title: formatText(L("ADM_NOTICE_NO_KEY"), notice.title),
        line: L("ADM_NOTICE_NO_KEY_LINE"),
        action: L("ADM_ADD_KEY"),
      };
    case "problem": {
      const parts = [
        L("ADM_AREA_HUB"),
        notice.module ? optional(L, `ADM_MODULE_${notice.module}`) : null,
        notice.status ? optional(L, `ADM_ISSUE_${notice.status}`) : null,
        plural(L, notice.count ?? 0, "ADM_N_OCCURRENCES_ONE", "ADM_N_OCCURRENCES", locale),
        plural(L, notice.users ?? 0, "ADM_N_USERS_ONE", "ADM_N_USERS", locale),
      ].filter(Boolean);
      return {
        icon: "circle-alert",
        tone: "danger",
        title: notice.title,
        line: parts.join(" · "),
        action: L("ADM_VIEW_PROBLEM"),
      };
    }
    default:
      return null;
  }
}
